import {
  ComponentMeta,
  ComponentRenderData,
  extractPlasmicQueryData,
  PlasmicComponent,
  PlasmicRootProvider,
} from '@plasmicapp/loader-nextjs';
import { getActiveVariation } from '@plasmicapp/loader-nextjs/edge';

import {
  TDrawScheduleSelect,
  TDrawTypePlanSelect,
} from '@daymade/api-contracts';
import { GameType } from '@daymade/common-lib/dbEnums';
import { TDrawTypeNumberSetup } from '@daymade/common-lib/dbJsonModels';
import { EntryMethod } from 'components/enter/EntryMethod';
import PopupManager from 'components/home-page/PopupManager';
import MainLayout from 'components/layout/MainLayout';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { observer } from 'mobx-react-lite';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { getChooseYourPlanDec2025Value, getEnterFlowProgressValue, getHomePageHeroTextSep2025Value } from 'utils/ab-test/abTests';
import { getMultistepTestGroup } from 'utils/ab-test/multistep/MultistepV5';
import { tryApi } from 'utils/api/common';
import { drawApiClient } from 'utils/apiClient';
import { runningInProduction } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';
import { PLASMIC } from '../plasmic-init';
import Custom404 from './404';

/**
 * Use fetchPages() to fetch list of pages that have been created in Plasmic
 */
export const getStaticPaths: GetStaticPaths = async () => {
  const pages = await PLASMIC.fetchPages();
  return {
    paths: pages.map((page) => ({
      params: { catchall: page.path.substring(1).split('/') },
    })),
    fallback: 'blocking',
  };
};

/**
 * For each page, pre-fetch the data we need to render it
 */
export const getStaticProps: GetStaticProps = async (context) => {
  // const prizes = await getLandingPagePrizes()
  const resp = await tryApi(
    drawApiClient.public.getDrawTypeCached({
      params: { draw_code: 'DAYMADEDRAW' },
    })
  );

  let upcomingDrawDate: string = new Date().toISOString();
  let drawTypePlans: TDrawTypePlanSelect[] = [];
  let drawSchedules: TDrawScheduleSelect[] = [];
  let numberSetup: TDrawTypeNumberSetup = {
    type: GameType.LOTTERY,
    main: [55, 55, 55, 55],
    bonus: [10],
  };

  if (resp.status == HttpStatusCode.OK) {
    if (resp.body.next_draw?.drawDate) {
      upcomingDrawDate = resp.body.next_draw?.drawDate.toISOString();
    }

    drawTypePlans = resp.body.draw_type_plans;
    drawSchedules = resp.body.draw_schedules;
    if (resp.body.draw_type.numberSetup) {
      numberSetup = resp.body.draw_type.numberSetup;
    }
  } else {
    console.log(resp);
  }

  const props = {
    hydrationData: {
      drawStore: {
        // We previously passed the upcomingDrawDate here, however, that caused the draw date to be cached
        // by our CDN, causing inconsistent draw dates to appear in the UI
        drawTypePlans: drawTypePlans,
        drawSchedules: drawSchedules,
        numberSetup: numberSetup,
      },
      prizeStore: {
        // landingPagePrizes: prizes,
      },
    },
  };
  const { catchall } = context.params ?? {};

  // Convert the catchall param into a path string
  const plasmicPath =
    typeof catchall === 'string'
      ? catchall
      : Array.isArray(catchall)
        ? `/${catchall.join('/')}`
        : '/';

  const plasmicComponent = [
    'Draw Countdown',
    'HomePrizeSection',
    'HomePrizeSection2',
    'Multi Step Order Summary',
    'Up Next',
  ];

  const plasmicData = await PLASMIC.maybeFetchComponentData(
    plasmicPath,
    ...plasmicComponent
  );

  if (!plasmicData) {
    // This is some non-Plasmic catch-all page
    return {
      props,
    };
  }

  // This is a path that Plasmic knows about.
  const pageMeta = plasmicData.entryCompMetas[0];
  // Cache the necessary data fetched for the page.
  const queryCache = await extractPlasmicQueryData(
    <PlasmicRootProvider
      loader={PLASMIC}
      prefetchedData={plasmicData}
      pageRoute={pageMeta.path}
      pageParams={pageMeta.params}
    >
      <PlasmicComponent component={pageMeta.displayName} />
    </PlasmicRootProvider>
  );

  // Pass the data in as props.
  return {
    props: { ...props, plasmicData, queryCache, plasmicPath },
  };
};

/**
 * Actually render the page!
 */
export const CatchallPage = observer(
  (props: {
    plasmicData?: ComponentRenderData;
    queryCache?: Record<string, any>;
    plasmicPath: string;
  }) => {
    const { plasmicData, queryCache, plasmicPath } = props;

    const router = useRouter();
    const enterStore = useEnterStore();
    const routerPathWithoutQuery = router.asPath.split('?')[0];
    useEffect(() => {
      if (routerPathWithoutQuery.startsWith('/enternow/')) {
        enterStore.setEntryMethod(EntryMethod.PremiumSubscription);
      }
    }, [routerPathWithoutQuery]);

    if (!plasmicData || plasmicData.entryCompMetas.length === 0) {
      return <Custom404 />;
    }
    const pageMeta = plasmicData.entryCompMetas[0];

    const traits: Record<string, string> = {};
    const multiStepTestGroup = getMultistepTestGroup();

    traits['MultistepV5Variant'] = multiStepTestGroup;
    traits['EnterFlowProgressVariant'] = getEnterFlowProgressValue();
    traits['ChooseYourPlanDec2025'] = getChooseYourPlanDec2025Value();
    traits['HomePageHeroTextSep2025'] = getHomePageHeroTextSep2025Value();

    const variation = getActiveVariation({
      splits: plasmicData.bundle.activeSplits,
      traits,
      path: plasmicPath,
    });

    return (
      // Pass in the data fetched in getStaticProps as prefetchedData
      <PlasmicRootProvider
        loader={PLASMIC}
        prefetchedData={runningInProduction ? plasmicData : undefined}
        prefetchedQueryData={queryCache}
        pageRoute={pageMeta.path}
        pageParams={pageMeta.params}
        pageQuery={router.query}
        variation={variation}
      >
        {
          // pageMeta.displayName contains the name of the component you fetched.
        }
        <WrapPlasmicPage pageMeta={pageMeta} />
      </PlasmicRootProvider>
    );
  }
);

function WrapPlasmicPage(props: {
  pageMeta: ComponentMeta & {
    params?: Record<string, string> | undefined;
  };
}) {
  const { pageMeta } = props;

  return (
    <MainLayout additionalClassName="homePage">
      <Head>
        <meta
          name="p:domain_verify"
          content="990ce7a8021498368b0c76701faec3b3"
        />
      </Head>

      <div id="top-of-page" />
      <PopupManager />
      <PlasmicComponent component={pageMeta.displayName} />
    </MainLayout>
  );
}

export default CatchallPage;
